import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ArrowUpDown, Star, TrendingUp, Clock, DollarSign } from 'lucide-react';
import { cn } from '@/lib/utils';
import { VendorLeaderboardEntry } from '@/hooks/useMarketplaceAnalytics';

interface VendorLeaderboardTableProps {
  vendors: VendorLeaderboardEntry[];
  isLoading: boolean;
}

type SortKey = 'quotesSubmitted' | 'quotesWon' | 'winRate' | 'avgResponseTimeHours' | 'totalGmvCents' | 'avgRating';

export function VendorLeaderboardTable({ vendors, isLoading }: VendorLeaderboardTableProps) {
  const [sortKey, setSortKey] = useState<SortKey>('totalGmvCents');
  const [sortAsc, setSortAsc] = useState(false);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(key);
      setSortAsc(false);
    }
  };

  const sortedVendors = [...vendors].sort((a, b) => {
    const aVal = a[sortKey] ?? 0;
    const bVal = b[sortKey] ?? 0;
    return sortAsc ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
  });

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(cents / 100);
  };

  const formatHours = (hours: number) => {
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    return `${days}d`;
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Vendor Performance Leaderboard</CardTitle>
          <CardDescription>Top vendors by GMV and performance metrics</CardDescription>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-64 w-full" />
        </CardContent>
      </Card>
    );
  }

  const SortableHeader = ({ label, sortKeyName }: { label: string; sortKeyName: SortKey }) => (
    <TableHead
      className="cursor-pointer hover:bg-muted/50"
      onClick={() => handleSort(sortKeyName)}
    >
      <div className="flex items-center gap-1">
        {label}
        <ArrowUpDown className={cn(
          "h-3 w-3",
          sortKey === sortKeyName ? "text-foreground" : "text-muted-foreground"
        )} />
      </div>
    </TableHead>
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Vendor Performance Leaderboard</CardTitle>
        <CardDescription>Top vendors by GMV and performance metrics</CardDescription>
      </CardHeader>
      <CardContent>
        {vendors.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No vendor data available
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8">#</TableHead>
                  <TableHead>Vendor</TableHead>
                  <SortableHeader label="Quotes" sortKeyName="quotesSubmitted" />
                  <SortableHeader label="Won" sortKeyName="quotesWon" />
                  <SortableHeader label="Win Rate" sortKeyName="winRate" />
                  <SortableHeader label="Avg Response" sortKeyName="avgResponseTimeHours" />
                  <SortableHeader label="Total GMV" sortKeyName="totalGmvCents" />
                  <SortableHeader label="Rating" sortKeyName="avgRating" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedVendors.map((vendor, index) => (
                  <TableRow key={vendor.vendorId}>
                    <TableCell className="font-medium text-muted-foreground">
                      {index + 1}
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="font-medium">{vendor.companyName}</div>
                        <div className="text-xs text-muted-foreground">{vendor.vendorName}</div>
                      </div>
                    </TableCell>
                    <TableCell>{vendor.quotesSubmitted}</TableCell>
                    <TableCell>{vendor.quotesWon}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <TrendingUp className={cn(
                          "h-3 w-3",
                          vendor.winRate >= 30 ? "text-green-500" : 
                          vendor.winRate >= 15 ? "text-amber-500" : "text-muted-foreground"
                        )} />
                        <span className={cn(
                          vendor.winRate >= 30 ? "text-green-500" : 
                          vendor.winRate >= 15 ? "text-amber-500" : ""
                        )}>
                          {vendor.winRate}%
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3 text-muted-foreground" />
                        {formatHours(vendor.avgResponseTimeHours)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 font-medium">
                        <DollarSign className="h-3 w-3 text-green-500" />
                        {formatCurrency(vendor.totalGmvCents)}
                      </div>
                    </TableCell>
                    <TableCell>
                      {vendor.avgRating ? (
                        <div className="flex items-center gap-1">
                          <Star className="h-3 w-3 text-amber-500 fill-amber-500" />
                          {vendor.avgRating.toFixed(1)}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
